/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  InputNumber,
  Modal,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { useSnackbar } from "notistack";
import {
  getOrgBulkApiSettings,
  updateOrgBulkApiSettings,
} from "../../utils/organization";
import {
  Gauge,
  Info,
  RotateCcw,
  Save,
  Zap,
  Shield,
  Rocket,
} from "lucide-react";

const AdminBulkApiModal = ({
  modalStatus,
  setModalStatus,
  organization,
  onSuccess,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [endpointsConfig, setEndpointsConfig] = useState({});
  const [isCustomized, setIsCustomized] = useState(false);

  const orgId = organization?.id;
  const orgName = organization?.account || organization?.name || "Organization";
  const provider = (organization?.provider || "xero").toLowerCase();
  const isQbo = provider === "qbo";

  const fetchSettings = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      const res = await getOrgBulkApiSettings(token, orgId, provider);
      if (res?.data?.success) {
        setEndpointsConfig(res.data.config || {});
        setIsCustomized(Boolean(res.data.is_customized));
      } else {
        enqueueSnackbar(res?.data?.message || "Failed to load Bulk API settings", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Error loading Bulk API settings",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalStatus && orgId) {
      fetchSettings();
    }
  }, [modalStatus, orgId]);

  const handleValueChange = (endpoint, type, newValue) => {
    setEndpointsConfig((prev) => {
      const currentEndpoint = prev[endpoint] || {};
      const currentType = currentEndpoint[type] || {};
      const clampedValue = Math.max(
        currentType.min || 1,
        Math.min(newValue || currentType.default, currentType.max || 1000)
      );

      return {
        ...prev,
        [endpoint]: {
          ...currentEndpoint,
          [type]: {
            ...currentType,
            value: clampedValue,
          },
        },
      };
    });
  };

  const handleResetSingle = (endpoint) => {
    setEndpointsConfig((prev) => {
      const current = prev[endpoint] || {};
      return {
        ...prev,
        [endpoint]: {
          backup: {
            ...current.backup,
            value: current.backup?.default || 100,
          },
          restore: {
            ...current.restore,
            value: current.restore?.default || (isQbo ? 30 : 25),
          },
        },
      };
    });
  };

  const applyPreset = (preset) => {
    setEndpointsConfig((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((ep) => {
        const item = updated[ep];
        if (!item) return;

        if (preset === "safe") {
          // Standard Safe
          updated[ep] = {
            ...item,
            backup: {
              ...item.backup,
              value: item.backup?.default || 100,
            },
            restore: {
              ...item.restore,
              value: item.restore?.default || (isQbo ? 30 : 25),
            },
          };
        } else if (preset === "turbo") {
          // High Performance
          const maxBackup = item.backup?.max || 1000;
          const maxRestore = item.restore?.max || 250;
          updated[ep] = {
            ...item,
            backup: {
              ...item.backup,
              value: Math.min(500, maxBackup),
            },
            restore: {
              ...item.restore,
              value: Math.min(isQbo ? 30 : 100, maxRestore),
            },
          };
        } else if (preset === "max") {
          // Maximum Permitted
          updated[ep] = {
            ...item,
            backup: {
              ...item.backup,
              value: item.backup?.max || 1000,
            },
            restore: {
              ...item.restore,
              value: item.restore?.max || (isQbo ? 30 : 250),
            },
          };
        }
      });
      return updated;
    });

    const presetNames = {
      safe: "Standard Safe",
      turbo: "High Performance",
      max: "Maximum Permitted",
    };
    enqueueSnackbar(`Applied ${presetNames[preset]} preset. Click Save to persist.`, {
      variant: "info",
    });
  };

  const handleResetAllToDefaults = async () => {
    setSaving(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      const res = await updateOrgBulkApiSettings(token, {
        org_id: orgId,
        provider,
        bulk_api_config: "reset",
      });
      if (res?.data?.success) {
        enqueueSnackbar("All endpoints reset to standard platform defaults", {
          variant: "success",
        });
        setIsCustomized(false);
        if (res.data.config) {
          setEndpointsConfig(res.data.config);
        }
        if (onSuccess) onSuccess();
      } else {
        enqueueSnackbar(res?.data?.message || "Failed to reset settings", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Error resetting settings",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      const payload = {
        org_id: orgId,
        provider,
        bulk_api_config: {
          [provider]: endpointsConfig,
        },
      };

      const res = await updateOrgBulkApiSettings(token, payload);
      if (res?.data?.success) {
        enqueueSnackbar("Bulk API batch sizing settings saved successfully!", {
          variant: "success",
        });
        setIsCustomized(true);
        if (onSuccess) onSuccess();
        setModalStatus(false);
      } else {
        enqueueSnackbar(res?.data?.message || "Failed to save settings", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Error saving Bulk API settings",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const tableData = Object.keys(endpointsConfig).map((key) => ({
    key,
    endpoint: key,
    backup: endpointsConfig[key]?.backup || {},
    restore: endpointsConfig[key]?.restore || {},
  }));

  const columns = [
    {
      title: "Endpoint / Entity",
      dataIndex: "endpoint",
      key: "endpoint",
      width: 220,
      render: (text) => (
        <div>
          <div className="font-semibold text-gray-900">{text}</div>
          <div className="text-xs text-gray-400 capitalize">{provider} API</div>
        </div>
      ),
    },
    {
      title: "Bulk Backup (GET)",
      key: "backup",
      width: 260,
      render: (_, record) => {
        const b = record.backup;
        const isDefault = b.value === b.default;
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <InputNumber
                min={b.min || 1}
                max={b.max || 1000}
                step={b.max > 200 ? 50 : 25}
                value={b.value}
                onChange={(val) => handleValueChange(record.endpoint, "backup", val)}
                className="w-28 font-medium"
              />
              <span className="text-xs text-gray-500">records / call</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Tag color="blue" className="mr-0 text-[10px] py-0 px-1">
                Min: {b.min} | Max: {b.max}
              </Tag>
              <span>Default: {b.default}</span>
              {!isDefault && (
                <Tag color="orange" className="mr-0 text-[10px] py-0 px-1">
                  Customized
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Bulk Restore (POST / Batch)",
      key: "restore",
      width: 260,
      render: (_, record) => {
        const r = record.restore;
        const isDefault = r.value === r.default;
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <InputNumber
                min={r.min || 1}
                max={r.max || 250}
                step={r.max > 50 ? 25 : 5}
                value={r.value}
                onChange={(val) => handleValueChange(record.endpoint, "restore", val)}
                className="w-28 font-medium"
              />
              <span className="text-xs text-gray-500">records / batch</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Tag color="purple" className="mr-0 text-[10px] py-0 px-1">
                Min: {r.min} | Max: {r.max}
              </Tag>
              <span>Default: {r.default}</span>
              {!isDefault && (
                <Tag color="orange" className="mr-0 text-[10px] py-0 px-1">
                  Customized
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Reset this endpoint to standard default">
          <Button
            type="text"
            size="small"
            icon={<RotateCcw className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />}
            onClick={() => handleResetSingle(record.endpoint)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      open={modalStatus}
      onCancel={() => setModalStatus(false)}
      width={940}
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
              <Gauge className="w-5 h-5" />
            </div>
            <div>
              <div className="text-base font-bold text-gray-900 leading-tight">
                Bulk API Batch Sizing Configuration
              </div>
              <div className="text-xs text-gray-500 font-normal">
                {orgName} &bull;{" "}
                <span className="capitalize font-semibold text-gray-700">
                  {provider}
                </span>{" "}
                (Org ID: {orgId})
              </div>
            </div>
          </div>
          {isCustomized ? (
            <Tag color="cyan" className="m-0 font-medium">
              Custom Sizing Active
            </Tag>
          ) : (
            <Tag color="default" className="m-0 font-medium">
              Default Sizing
            </Tag>
          )}
        </div>
      }
      footer={
        <div className="flex items-center justify-between w-full pt-2">
          <Button
            danger
            type="text"
            icon={<RotateCcw className="w-4 h-4 mr-1 inline" />}
            onClick={handleResetAllToDefaults}
            loading={saving}
          >
            Reset All to Defaults
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={() => setModalStatus(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<Save className="w-4 h-4 mr-1 inline" />}
              onClick={handleSave}
              loading={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Save Sizing Settings
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        {/* Informational Box */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 flex items-start gap-2.5">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="leading-relaxed">
            <span className="font-semibold text-slate-900">
              Zero Missed Transactions Safeguard:
            </span>{" "}
            Increasing batch size accelerates operations. If any batch
            encounters timeouts or errors, the restore engine automatically
            decomposes that chunk into 1-by-1 retries. Completed records are
            permanently recorded in the ID mapping table, guaranteeing zero
            duplicates on resume.
          </div>
        </div>

        {/* Quick 1-Click Presets */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            Quick 1-Click Presets:
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="small"
              icon={<Shield className="w-3.5 h-3.5 text-emerald-600 mr-1 inline" />}
              onClick={() => applyPreset("safe")}
            >
              Standard Safe (Default)
            </Button>
            <Button
              size="small"
              icon={<Zap className="w-3.5 h-3.5 text-blue-600 mr-1 inline" />}
              onClick={() => applyPreset("turbo")}
            >
              High Performance (500 GET / 100 POST)
            </Button>
            <Button
              size="small"
              icon={<Rocket className="w-3.5 h-3.5 text-purple-600 mr-1 inline" />}
              onClick={() => applyPreset("max")}
            >
              Maximum Permitted (1,000 GET / Max POST)
            </Button>
          </div>
        </div>

        {/* Table of Endpoints */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center">
              <Spin size="large" />
              <div className="text-xs text-gray-500 mt-2">
                Loading endpoint limits...
              </div>
            </div>
          ) : (
            <Table
              dataSource={tableData}
              columns={columns}
              pagination={false}
              size="small"
              scroll={{ y: 380 }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AdminBulkApiModal;
